import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, Chip, TextField,
  InputAdornment, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Switch, Grid, Snackbar, Alert, Tooltip,
  ToggleButton, ToggleButtonGroup, LinearProgress, CircularProgress
} from '@mui/material';
import {
  Add, Edit, Delete, Search, OpenInNew, PushPin,
  PictureAsPdf, Description, TableChart, Image, Link as LinkIcon,
  InsertDriveFile, CloudUpload, Refresh
} from '@mui/icons-material';
import { CATEGORIES, FILE_TYPES } from '../../config/resourcesConfig';
import apiClient from '../../services/apiClient';

const FILE_ICONS = {
  pdf:   { icon: PictureAsPdf,    color: '#e53935' },
  docx:  { icon: Description,     color: '#1565c0' },
  xlsx:  { icon: TableChart,      color: '#2e7d32' },
  image: { icon: Image,           color: '#6a1b9a' },
  link:  { icon: LinkIcon,        color: '#00838f' },
  other: { icon: InsertDriveFile, color: '#546e7a' },
};

const ROLE_LABELS = { student: 'Student', teacher: 'Teacher', parent: 'Parent' };

const CONTENT_TYPES = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  image: 'image/*',
  other: 'application/octet-stream',
};

const blankDoc = () => ({
  name: '',
  description: '',
  category: 'Other',
  file_type: 'pdf',
  s3_url: '',
  s3_key: '',
  file_size: '',
  visible_to: ['all'],
  pinned: false,
});

const formatDate = (ts) => ts ? new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

const ResourcesAdmin = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [editItem, setEditItem] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const fileInputRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiClient.getDocuments();
      setDocuments(data.documents || []);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load documents: ' + err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = documents.filter(r => {
    const matchSearch = !search || r.name.toLowerCase().includes(search.toLowerCase());
    const matchCat = catFilter === 'all' || r.category === catFilter;
    return matchSearch && matchCat;
  });

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    // Auto-detect file type
    const ext = file.name.split('.').pop().toLowerCase();
    const typeMap = { pdf: 'pdf', doc: 'docx', docx: 'docx', xls: 'xlsx', xlsx: 'xlsx', png: 'image', jpg: 'image', jpeg: 'image', gif: 'image' };
    const detectedType = typeMap[ext] || 'other';
    // Auto-fill name if blank
    const displayName = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
    const sizeLabel = file.size < 1024 * 1024
      ? `${(file.size / 1024).toFixed(0)} KB`
      : `${(file.size / (1024 * 1024)).toFixed(1)} MB`;
    setEditItem(p => ({
      ...p,
      file_type: detectedType,
      file_size: sizeLabel,
      name: p.name || displayName,
    }));
  };

  const handleSave = async () => {
    if (!editItem.name.trim()) {
      setSnackbar({ open: true, message: 'Name is required.', severity: 'error' });
      return;
    }
    if (isNew && !selectedFile && editItem.file_type !== 'link' && !editItem.s3_url) {
      setSnackbar({ open: true, message: 'Please select a file to upload or paste a URL.', severity: 'error' });
      return;
    }
    if (editItem.file_type === 'link' && !editItem.s3_url) {
      setSnackbar({ open: true, message: 'URL is required for link resources.', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      let s3Key = editItem.s3_key;
      let s3Url = editItem.s3_url;

      // Upload file to S3 if one was selected
      if (selectedFile) {
        setUploadProgress(0);
        const { uploadUrl, s3Key: key, publicUrl } = await apiClient.getDocumentUploadUrl(
          selectedFile.name,
          selectedFile.type || CONTENT_TYPES[editItem.file_type] || 'application/octet-stream',
          editItem.category
        );

        // Upload directly to S3 via presigned URL
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
          };
          xhr.onload = () => xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
          xhr.onerror = () => reject(new Error('Upload failed'));
          xhr.open('PUT', uploadUrl);
          xhr.setRequestHeader('Content-Type', selectedFile.type || 'application/octet-stream');
          xhr.send(selectedFile);
        });

        s3Key = key;
        s3Url = publicUrl;
        setUploadProgress(null);
      }

      if (isNew) {
        await apiClient.createDocument({
          name: editItem.name.trim(),
          description: editItem.description || null,
          category: editItem.category,
          file_type: editItem.file_type,
          s3_key: s3Key || `links/${Date.now()}`,
          s3_url: s3Url,
          file_size: editItem.file_size || null,
          visible_to: editItem.visible_to,
          pinned: editItem.pinned,
        });
        setSnackbar({ open: true, message: 'Document added successfully.', severity: 'success' });
      } else {
        await apiClient.updateDocument(editItem.document_id, {
          name: editItem.name.trim(),
          description: editItem.description || null,
          category: editItem.category,
          file_type: editItem.file_type,
          file_size: editItem.file_size || null,
          visible_to: editItem.visible_to,
          pinned: editItem.pinned,
        });
        setSnackbar({ open: true, message: 'Document updated.', severity: 'success' });
      }

      setEditItem(null);
      setSelectedFile(null);
      await load();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
      setUploadProgress(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"? This will remove the file from S3.`)) return;
    try {
      await apiClient.deleteDocument(doc.document_id);
      setDocuments(prev => prev.filter(d => d.document_id !== doc.document_id));
      setSnackbar({ open: true, message: 'Document deleted.', severity: 'info' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleTogglePin = async (doc) => {
    try {
      const updated = await apiClient.updateDocument(doc.document_id, { pinned: !doc.pinned });
      setDocuments(prev => prev.map(d => d.document_id === doc.document_id ? updated : d));
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const usedCats = CATEGORIES.filter(c => documents.some(r => r.category === c));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Resources & Downloads</Typography>
          <Typography variant="body2" color="text.secondary">
            Upload files to S3 and control which portals can see them.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setEditItem(blankDoc()); setIsNew(true); setSelectedFile(null); }}>
            Add Document
          </Button>
        </Box>
      </Box>

      <Paper elevation={2} sx={{ borderRadius: 2, mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search documents…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            size="small"
            sx={{ minWidth: 240 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Category</InputLabel>
            <Select value={catFilter} label="Category" onChange={e => setCatFilter(e.target.value)}>
              <MenuItem value="all">All Categories</MenuItem>
              {usedCats.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          <Box sx={{ ml: 'auto' }}>
            <Chip label={`${documents.length} total`} size="small" />
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Document</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Uploaded</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Visible To</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 130 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(r => {
                  const fi = FILE_ICONS[r.file_type] || FILE_ICONS.other;
                  const FileIcon = fi.icon;
                  return (
                    <TableRow key={r.document_id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FileIcon sx={{ color: fi.color, fontSize: 18, flexShrink: 0 }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {r.pinned && <PushPin sx={{ fontSize: 12, mr: 0.5, transform: 'rotate(45deg)', color: 'primary.main' }} />}
                              {r.name}
                            </Typography>
                            {r.description && (
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 260, display: 'block' }}>
                                {r.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell><Chip label={r.category} size="small" variant="outlined" /></TableCell>
                      <TableCell><Typography variant="caption">{r.file_size || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{formatDate(r.uploaded_at)}</Typography></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {(r.visible_to || []).includes('all')
                            ? <Chip label="All" size="small" color="success" />
                            : (r.visible_to || []).map(v => <Chip key={v} label={ROLE_LABELS[v] || v} size="small" />)
                          }
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title={r.pinned ? 'Unpin' : 'Pin to top'}>
                            <IconButton size="small" onClick={() => handleTogglePin(r)} color={r.pinned ? 'primary' : 'default'}>
                              <PushPin sx={{ fontSize: 16, transform: r.pinned ? 'rotate(45deg)' : 'none' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Open">
                            <IconButton size="small" component="a" href={r.s3_url} target="_blank" rel="noopener noreferrer">
                              <OpenInNew fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => { setEditItem({ ...r }); setIsNew(false); setSelectedFile(null); }}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDelete(r)} color="error">
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filtered.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      {documents.length === 0 ? 'No documents yet. Click "Add Document" to upload the first one.' : 'No documents match your search.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add / Edit Dialog */}
      <Dialog open={!!editItem} onClose={() => { setEditItem(null); setSelectedFile(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{isNew ? 'Add Document' : 'Edit Document'}</DialogTitle>
        {editItem && (
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>

              {/* File upload (new only) */}
              {isNew && editItem.file_type !== 'link' && (
                <Grid item xs={12}>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<CloudUpload />}
                    onClick={() => fileInputRef.current.click()}
                    sx={{ py: 1.5, borderStyle: 'dashed' }}
                  >
                    {selectedFile ? selectedFile.name : 'Click to select a file'}
                  </Button>
                  {uploadProgress !== null && (
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress variant="determinate" value={uploadProgress} />
                      <Typography variant="caption" color="text.secondary">{uploadProgress}% uploaded</Typography>
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Or switch File Type to "External Link" to paste a URL instead.
                  </Typography>
                </Grid>
              )}

              {/* Link URL (only shown for link type) */}
              {editItem.file_type === 'link' && (
                <Grid item xs={12}>
                  <TextField fullWidth required label="URL" value={editItem.s3_url}
                    onChange={e => setEditItem(p => ({ ...p, s3_url: e.target.value }))}
                    placeholder="https://example.com/document" />
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField fullWidth required label="Display Name" value={editItem.name}
                  onChange={e => setEditItem(p => ({ ...p, name: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Description" value={editItem.description || ''}
                  onChange={e => setEditItem(p => ({ ...p, description: e.target.value }))}
                  helperText="Optional — shown below the name in portals" />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select value={editItem.category} label="Category"
                    onChange={e => setEditItem(p => ({ ...p, category: e.target.value }))}>
                    {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>File Type</InputLabel>
                  <Select value={editItem.file_type} label="File Type"
                    onChange={e => setEditItem(p => ({ ...p, file_type: e.target.value }))}>
                    {FILE_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              {!isNew && (
                <Grid item xs={12}>
                  <TextField fullWidth label="File Size" value={editItem.file_size || ''}
                    onChange={e => setEditItem(p => ({ ...p, file_size: e.target.value }))}
                    placeholder="e.g. 1.2 MB" helperText="Display label only" />
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Visible To</Typography>
                <ToggleButtonGroup
                  value={editItem.visible_to.includes('all') ? ['all'] : editItem.visible_to}
                  onChange={(e, val) => {
                    if (!val || val.length === 0) return;
                    if (val.includes('all') && !editItem.visible_to.includes('all')) {
                      setEditItem(p => ({ ...p, visible_to: ['all'] }));
                    } else {
                      setEditItem(p => ({ ...p, visible_to: val.filter(v => v !== 'all') }));
                    }
                  }}
                  size="small"
                >
                  <ToggleButton value="all">Everyone</ToggleButton>
                  <ToggleButton value="student">Students</ToggleButton>
                  <ToggleButton value="teacher">Teachers</ToggleButton>
                  <ToggleButton value="parent">Parents</ToggleButton>
                </ToggleButtonGroup>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={editItem.pinned} onChange={e => setEditItem(p => ({ ...p, pinned: e.target.checked }))} />}
                  label="Pin to top of category"
                />
              </Grid>
            </Grid>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => { setEditItem(null); setSelectedFile(null); }} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
            {saving ? (uploadProgress !== null ? `Uploading ${uploadProgress}%…` : 'Saving…') : (isNew ? 'Upload & Save' : 'Save Changes')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ResourcesAdmin;
